/**
 * Runtime brand flag.
 *
 * The server declares the active brand via the PUBLIC GET /api/brand endpoint
 * (driven by the PSOS_BRAND env var). The client applies it as
 * `<html data-brand="…">` so a future white-label stylesheet can key on it
 * without a rebuild. Only the default brand ships today.
 *
 * Called from index.jsx before ReactDOM renders anything.
 */

import api from './services/api.js';

const STORAGE_KEY = 'brand';
const VALID_BRANDS = ['default'];

function applyBrand(brand) {
  if (!VALID_BRANDS.includes(brand)) return;
  document.documentElement.dataset.brand = brand;
  try {
    localStorage.setItem(STORAGE_KEY, brand);
  } catch {
    /* storage may be unavailable (private mode) — the brand still applies */
  }
}

/** The brand active right now (read per render, never cached at module scope). */
export function currentBrand() {
  if (typeof document === 'undefined') return 'default';
  return document.documentElement.dataset.brand || 'default';
}

export function initBrand() {
  // Cached value first — synchronous, so the first paint is already branded.
  let cached = null;
  try {
    cached = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  applyBrand(cached && VALID_BRANDS.includes(cached) ? cached : 'default');

  // Server truth — public endpoint, so this works on the sign-in page too.
  api
    .request('/brand')
    .then((data) => {
      if (data && VALID_BRANDS.includes(data.brand)) applyBrand(data.brand);
    })
    .catch(() => {
      /* offline / API down: keep whatever was cached; never block the app */
    });
}
